import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { uploadMany, fileUrl } from "@/lib/uploads";
import { Plus, Trash2, Camera, Image as ImageIcon } from "lucide-react";
import ProjectSearchBar, { ProjectSearchEmpty, projectMatches } from "@/components/shared/ProjectSearchBar";

/**
 * Production Floor & Procurement Matrix.
 * - Procurement Manager: edits raw_material_rows [Sl No | Material Name | Qty]
 * - Production Manager: uploads assembly photos tagged to specific rows
 * - Others factory-side: read-only
 */
export default function ProductionPage() {
  const { isAdmin, roleName, roleType } = useAuth();
  const isProcMgr = isAdmin || roleName === "Procurement Manager" || roleType === "director";
  const isProdMgr = isAdmin || roleName === "Production Manager" || roleType === "director";
  const canEdit = isProcMgr;
  const canPhotos = isProdMgr || isProcMgr;

  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const filteredOrders = useMemo(
    () => orders.filter((o) => projectMatches(o, q)),
    [orders, q]
  );

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/procurement/orders");
      setOrders(data);
      if (!selectedId && data.length > 0) setSelectedId(data[0].id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [selectedId]);
  const loadSelected = useCallback(async (id) => {
    if (!id) return;
    const { data } = await api.get(`/procurement/orders/${id}`);
    setSelected(data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSelected(selectedId); }, [selectedId, loadSelected]);

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
          Phase 6 · {isProcMgr ? "Procurement Matrix" : "Production Floor"}
        </p>
        <h1 className="font-display text-3xl text-white mt-1">Materials & Assembly</h1>
      </div>

      <div className="px-10 py-6 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 items-start">
        <div className="space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">Orders</p>
          <ProjectSearchBar
            value={q}
            onChange={setQ}
            resultCount={filteredOrders.length}
            totalCount={orders.length}
            testid="production-search"
          />
          {orders.length === 0 && (
            <div className="iz-card p-6 text-center text-xs text-gray-500">No factory orders.</div>
          )}
          {orders.length > 0 && filteredOrders.length === 0 && q.trim() && (
            <ProjectSearchEmpty query={q} testid="production-search-empty" />
          )}
          {filteredOrders.map(o => (
            <button key={o.id} onClick={() => setSelectedId(o.id)}
                    className={`iz-card p-3 w-full text-left hover:border-[#d4af37]/40 ${selectedId === o.id ? "border-[#d4af37]" : ""}`}
                    data-testid={`prod-row-${o.id}`}>
              <p className="text-white text-sm truncate">{o.client_name}{o.city ? ` · ${o.city}` : ""}</p>
              <p className="text-[11px] text-gray-500 truncate">{o.project_name}</p>
            </button>
          ))}
        </div>

        <div className="min-w-0 space-y-4">
          {selected ? (
            <>
              <RawMatrix order={selected} canEdit={canEdit} onRefresh={() => loadSelected(selectedId)} />
              <ProductionPhotos order={selected} canPhotos={canPhotos} onRefresh={() => loadSelected(selectedId)} />
            </>
          ) : (
            <div className="iz-card p-12 text-center text-sm text-gray-500">Select an order.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function RawMatrix({ order, canEdit, onRefresh }) {
  const [rows, setRows] = useState(order.raw_material_rows || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setRows(order.raw_material_rows || []); }, [order.id, order.raw_material_rows]);

  const localUpdate = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = async () => {
    try {
      const nextSl = rows.length ? Math.max(...rows.map(r => r.sl_no || 0)) + 1 : 1;
      const { data } = await api.post(`/procurement/orders/${order.id}/raw-materials`, { sl_no: nextSl, material_name: "", qty: 1 });
      setRows([...rows, data]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveRow = async (row) => {
    setSaving(true);
    try {
      await api.patch(`/procurement/orders/${order.id}/raw-materials/${row.id}`, {
        sl_no: Number(row.sl_no) || 0, material_name: row.material_name, qty: Number(row.qty) || 0,
      });
      toast.success("Saved");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const delRow = async (row) => {
    if (!window.confirm("Delete row?")) return;
    try {
      await api.delete(`/procurement/orders/${order.id}/raw-materials/${row.id}`);
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="iz-card p-5" data-testid="raw-matrix">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Procurement Matrix</p>
          <p className="text-[10px] font-mono text-gray-600 mt-0.5">Sl No · Material Name · Qty</p>
        </div>
        {canEdit && (
          <button onClick={addRow} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="add-raw-row">
            <Plus className="w-3.5 h-3.5"/>Add row
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-gray-500 italic">No rows yet.</p>
      ) : (
        <div className="overflow-x-auto -mx-1">
        <table className="w-full text-sm min-w-[480px]">
          <thead>
            <tr className="text-[9px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#1a1a1a]">
              <th className="text-left py-2 w-20">Sl No</th>
              <th className="text-left py-2">Material Name</th>
              <th className="text-right py-2 w-32">Qty</th>
              {canEdit && <th className="w-24"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="border-b border-[#0f0f0f]" data-testid={`raw-row-${r.id}`}>
                <td className="py-2 pr-2">
                  <input type="number" value={r.sl_no || ""} onChange={(e) => localUpdate(i, { sl_no: e.target.value })}
                         onBlur={() => canEdit && saveRow(r)} disabled={!canEdit}
                         className="bg-transparent border-b border-[#1a1a1a] focus:border-[#d4af37] text-xs text-white outline-none py-1 w-16"/>
                </td>
                <td className="py-2 pr-2">
                  <input type="text" value={r.material_name || ""} onChange={(e) => localUpdate(i, { material_name: e.target.value })}
                         onBlur={() => canEdit && saveRow(r)} disabled={!canEdit}
                         placeholder="Material name"
                         className="w-full bg-transparent border-b border-[#1a1a1a] focus:border-[#d4af37] text-sm text-white placeholder-gray-600 outline-none py-1"/>
                </td>
                <td className="py-2 pr-2 text-right">
                  <input type="number" value={r.qty || ""} onChange={(e) => localUpdate(i, { qty: e.target.value })}
                         onBlur={() => canEdit && saveRow(r)} disabled={!canEdit}
                         className="bg-transparent border-b border-[#1a1a1a] focus:border-[#d4af37] text-xs text-white outline-none py-1 w-24 text-right"/>
                </td>
                {canEdit && (
                  <td className="py-2 pl-2">
                    <button onClick={() => delRow(r)} className="p-1 text-gray-500 hover:text-red-400" data-testid={`del-raw-${r.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {saving && <p className="text-[10px] font-mono text-gray-500 mt-2">Saving…</p>}
    </div>
  );
}

function ProductionPhotos({ order, canPhotos, onRefresh }) {
  const [tag, setTag] = useState("");
  const [tagRowId, setTagRowId] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const uploaded = await uploadMany(Array.from(files));
      await api.post(`/procurement/orders/${order.id}/production-photos`, {
        raw_material_row_id: tagRowId || null,
        file_ids: uploaded.map(u => u.id),
        tag: tag || null,
      });
      toast.success("Photos uploaded");
      onRefresh();
      setTag(""); setTagRowId("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="iz-card p-5" data-testid="prod-photos">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Assembly Floor Photos</p>
        {canPhotos && (
          <div className="flex items-center gap-2">
            <select value={tagRowId} onChange={(e) => setTagRowId(e.target.value)} className="iz-input text-xs !py-1">
              <option value="">Tag to row… (optional)</option>
              {(order.raw_material_rows || []).map(r => (
                <option key={r.id} value={r.id}>#{r.sl_no} · {r.material_name}</option>
              ))}
            </select>
            <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Tag / caption" className="iz-input text-xs !py-1"/>
            <button onClick={() => inputRef.current?.click()} disabled={busy} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="upload-prod-photos">
              <Camera className="w-3.5 h-3.5"/>{busy ? "Uploading…" : "Upload"}
            </button>
            <input ref={inputRef} type="file" hidden multiple accept="image/*" onChange={(e) => upload(e.target.files)}/>
          </div>
        )}
      </div>

      {(order.production_photos || []).length === 0 ? (
        <p className="text-xs text-gray-500 italic">No photos yet.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(order.production_photos || []).map(p => (
            <div key={p.id} className="space-y-1" data-testid={`prod-photo-${p.id}`}>
              <div className="grid grid-cols-2 gap-1">
                {(p.file_ids || []).map(fid => (
                  <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer"
                     className="block aspect-square bg-[#0a0a0a] border border-[#1a1a1a] rounded overflow-hidden hover:border-[#d4af37]/40">
                    <img src={fileUrl(fid)} alt="" className="w-full h-full object-cover"/>
                  </a>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <ImageIcon className="w-3 h-3 text-[#d4af37]"/>
                <p className="text-[10px] font-mono text-gray-500 truncate">{p.tag || "untagged"}</p>
              </div>
              <p className="text-[9px] font-mono text-gray-600">{new Date(p.uploaded_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
